import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FlowNode } from '../../types';
import { getFlows } from '../../api/flows';

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

export function SubFlowStepConfig({ node, onUpdate }: Props) {
  const config = (node.config ?? {}) as Record<string, unknown>;

  const [alias, setAlias] = useState((config.alias as string) ?? '');
  const [flowId, setFlowId] = useState((config.flowId as string) ?? '');
  const [bodyTemplate, setBodyTemplate] = useState((config.body as string) ?? '');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAlias((config.alias as string) ?? '');
    setFlowId((config.flowId as string) ?? '');
    setBodyTemplate((config.body as string) ?? '');
  }, [node.id]);

  const { data: flows } = useQuery({ queryKey: ['flows'], queryFn: getFlows });

  useEffect(() => {
    const { alias: _a, flowId: _f, body: _b, ...rest } = config;
    const next = {
      ...rest,
      ...(alias.trim() ? { alias: alias.trim() } : {}),
      ...(flowId ? { flowId } : {}),
      ...(bodyTemplate.trim() ? { body: bodyTemplate.trim() } : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, flowId, bodyTemplate]);

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-y-auto">

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. trigger-onboarding"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            Child execution id via <span className="font-mono text-tone-blue">{`{{${alias}.executionId}}`}</span>
          </p>
        )}
      </div>

      {/* Target flow */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Target flow</label>
        <select
          className="border border-input rounded p-1.5 text-xs bg-background"
          value={flowId}
          onChange={e => setFlowId(e.target.value)}
        >
          <option value="">— select a flow —</option>
          {flows?.map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          The flow that is triggered once when this step runs.
        </p>
      </div>

      {/* Body template */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">
          Body template <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <textarea
          className="w-full border border-input rounded p-1.5 text-xs font-mono bg-background min-h-[120px] resize-y"
          placeholder={'{\n  "taskId": "{{body.entity.id}}",\n  "status": "{{body.status}}"\n}'}
          value={bodyTemplate}
          onChange={e => setBodyTemplate(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          JSON template for the sub-flow's body. If empty, the current flow's body is forwarded unchanged.
        </p>
      </div>
    </div>
  );
}
